import React from "react";
import { Badge, Box, Button, Divider, Drawer, DrawerBody, DrawerCloseButton, DrawerContent, DrawerHeader, DrawerOverlay, Flex, FormControl, FormLabel, Heading, HStack, Input, Select, SimpleGrid, Spacer, Spinner, Stack, Text } from "@chakra-ui/react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, LineChart, Line } from "recharts";
import { ChartType } from "./types";
import { MultiSelectDropdown } from "./MultiSelectDropdown";
import { VirtualizedReportTable } from "./VirtualisedReportTable";
function defaultParamsFromFilters(filters) {
    var _a;
    const out = {};
    for (const f of filters)
        out[f.id] = (_a = f.defaultValue) !== null && _a !== void 0 ? _a : "";
    return out;
}
function splitMulti(value) {
    return value
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);
}
function joinMulti(values) {
    // keep stable ordering
    const uniq = Array.from(new Set(values));
    return uniq.join(",");
}
function safeText(v) {
    if (v === null || v === undefined)
        return "";
    return String(v);
}
export class DashboardShell extends React.Component {
    constructor() {
        super(...arguments);
        this.state = {
            params: defaultParamsFromFilters(this.props.report.filters),
            rows: [],
            loading: false,
            err: "",
            isOpen: false,
            selectedRow: null
        };
        this.setParam = (key, value) => {
            this.setState((s) => ({ params: Object.assign(Object.assign({}, s.params), { [key]: value }) }));
        };
        this.reload = async () => {
            const { report } = this.props;
            const { params } = this.state;
            this.setState({ loading: true, err: "" });
            try {
                const data = await report.fetcher(params);
                this.setState({ rows: data, loading: false });
            }
            catch (e) {
                this.setState({
                    err: (e === null || e === void 0 ? void 0 : e.message) || "Failed to load data",
                    rows: [],
                    loading: false
                });
            }
        };
        this.applyClientFilters = (rows, vreload = false) => {
            var _a, _b;
            const { report } = this.props;
            const { params } = this.state;
            let out = [...rows];
            for (const f of report.filters) {
                const val = ((_a = params[f.id]) !== null && _a !== void 0 ? _a : "").trim();
                if (!val)
                    continue;
                if (f.type === "search") {
                    const q = val.toLowerCase();
                    out = out.filter((r) => JSON.stringify(r).toLowerCase().includes(q));
                }
                else if (f.type === "select" || f.type === "multiselect") {
                    const selected = f.type === "multiselect" ? splitMulti(val) : [val];
                    out = out.filter((r) => {
                        const anyRow = r;
                        // Prefer matching a row field with the same id (plant/status/materialType etc.)
                        if (anyRow[f.id] !== undefined) {
                            const rowVal = safeText(anyRow[f.id]);
                            console.log(rowVal + " search " + f.id + " " + selected);
                            return selected.includes(rowVal);
                        }
                        // Fallback: JSON match (less ideal, but keeps it generic)
                        return selected.some((x) => JSON.stringify(r).includes(x));
                    });
                }
                else if (f.type === "date") {
                    out = out.filter((r) => {
                        const anyRow = r;
                        const rowDate = safeText(anyRow.odate);
                        if (!rowDate)
                            return true;
                        console.log(rowDate + " " + val);
                        if (f.id.toLowerCase().includes("from"))
                            return rowDate >= val;
                        if (f.id.toLowerCase().includes("to"))
                            return rowDate <= val;
                        return true;
                    });
                }
                else if (f.type === "number" && vreload) {
                    if (!val)
                        continue;
                    const [op, rest = ""] = val.split(":");
                    const field = (_b = f.field) !== null && _b !== void 0 ? _b : f.id;
                    out = out.filter((r) => {
                        const anyRow = r;
                        const n = Number(anyRow[field]);
                        console.log("val " + rest + " " + op);
                        if (Number.isNaN(n))
                            return false;
                        if (op === "gt")
                            return n > Number(rest);
                        if (op === "gte")
                            return n >= Number(rest);
                        if (op === "lt")
                            return n < Number(rest);
                        if (op === "lte")
                            return n <= Number(rest);
                        if (op === "eq")
                            return n === Number(rest);
                        if (op === "between") {
                            const parts = rest.split(",");
                            const a = Number(parts[0]);
                            const b = Number(parts[1]);
                            if (Number.isNaN(a) || Number.isNaN(b))
                                return true; // ignore incomplete
                            return n >= a && n <= b;
                        }
                        return true;
                    });
                }
            }
            return out;
        };
        this.numberDebounceTimers = {};
        this.debouncedSetNumberParam = (id, value, delay = 1000) => {
            this.setParam(id, value);
        };
        this.openDetails = (row) => {
            if (!this.props.report.rowDetails)
                return;
            this.setState({ selectedRow: row, isOpen: true });
        };
        this.closeDetails = () => {
            this.setState({ isOpen: false });
        };
    }
    componentDidMount() {
        void this.reload();
    }
    componentDidUpdate(prevProps) {
        if (prevProps.report.id !== this.props.report.id) {
            this.setState({
                params: defaultParamsFromFilters(this.props.report.filters),
                rows: [],
                err: "",
                isOpen: false,
                selectedRow: null
            }, () => void this.reload());
        }
    }
    Card({ title, children }) {
        return (React.createElement(Box, { borderWidth: "1px", borderRadius: "md", p: 3, bg: "white" },
            React.createElement(Heading, { size: "xs", mb: 2, color: "gray.700" }, title),
            React.createElement(Box, { h: "220px" }, children)));
    }
    buildBar(def) {
        var _a;
        return React.createElement(Box, { borderWidth: "1px", borderRadius: "md", p: 3, bg: "white" },
            React.createElement(Heading, { size: "xs", mb: 2, color: "gray.700" }, def.title),
            React.createElement(Box, { h: "220px" },
                React.createElement(ResponsiveContainer, { width: "100%", height: "100%" },
                    React.createElement(BarChart, { data: def.points },
                        React.createElement(XAxis, { dataKey: def.xdatakey }),
                        React.createElement(YAxis, null),
                        React.createElement(Tooltip, null),
                        React.createElement(Bar, { dataKey: def.ydatakey, fill: (_a = def.fill) !== null && _a !== void 0 ? _a : "#8884d8" })))));
    }
    buildLine(def) {
        var _a;
        const labelmap = new Map();
        (_a = def.labels) === null || _a === void 0 ? void 0 : _a.forEach((l, index) => {
            var _a, _b, _c, _d;
            if (index == 0) {
                labelmap.set(def.ydatakey, l);
            }
            else if (((_b = (_a = def.ymorekyes) === null || _a === void 0 ? void 0 : _a.length) !== null && _b !== void 0 ? _b : 0) > index) {
                let k = (_d = (_c = def.ymorekyes) === null || _c === void 0 ? void 0 : _c[index]) !== null && _d !== void 0 ? _d : "";
                if (k) {
                    labelmap.set(k, l);
                }
            }
        });
        const renderCustomLabel = (props) => {
            var _a;
            const { x, y, dataKey, value } = props;
            return (React.createElement("text", { x: x, y: y - 5, fill: "#333" }, (_a = labelmap.get(dataKey)) !== null && _a !== void 0 ? _a : ''));
        };
        return React.createElement(Box, { borderWidth: "1px", borderRadius: "md", p: 3, bg: "white" },
            React.createElement(Heading, { size: "xs", mb: 2, color: "gray.700" }, def.title),
            React.createElement(Box, { h: "220px" },
                React.createElement(ResponsiveContainer, { width: "100%", height: "100%" },
                    React.createElement(LineChart, { data: def.points },
                        React.createElement(XAxis, { dataKey: def.xdatakey }),
                        React.createElement(YAxis, null),
                        React.createElement(Tooltip, null),
                        React.createElement(Line, { type: "monotone", stroke: def.fill, label: renderCustomLabel, dataKey: def.ydatakey, dot: false }),
                        def.ymorekyes && def.ymorekyes.map((key, index) => { var _a, _b, _c, _d; return React.createElement(Line, { label: renderCustomLabel, stroke: (((_b = (_a = def.otherfills) === null || _a === void 0 ? void 0 : _a.length) !== null && _b !== void 0 ? _b : 0) > index ? ((_d = (_c = def.otherfills) === null || _c === void 0 ? void 0 : _c[index]) !== null && _d !== void 0 ? _d : "") : ""), type: "monotone", dataKey: key, dot: false }); })))));
    }
    builcCharts(defs) {
        // lets build it here and we done!
        return React.createElement(SimpleGrid, { columns: { base: 1, md: 2 }, spacing: 3 }, defs.map((def) => def.type === ChartType.Bar ? this.buildBar(def) : this.buildLine(def)));
    }
    render() {
        var _a, _b, _c, _d;
        const { report } = this.props;
        const { params, rows, loading, err, isOpen, selectedRow } = this.state;
        const filteredRows = this.applyClientFilters(rows);
        const kpis = (_a = report.kpis) !== null && _a !== void 0 ? _a : [];
        return (React.createElement(Box, { bg: "white", borderWidth: "1px", borderRadius: "md", p: 4 },
            React.createElement(Flex, { align: { base: "start", md: "center" }, direction: { base: "column", md: "row" }, gap: 2 },
                React.createElement(Box, null,
                    React.createElement(Text, { fontWeight: "semibold" }, report.title),
                    report.subtitle ? (React.createElement(Text, { fontSize: "sm", color: "gray.600" }, report.subtitle)) : null),
                React.createElement(Spacer, null),
                React.createElement(HStack, { spacing: 2, wrap: "wrap" },
                    kpis.map((k) => {
                        var _a;
                        return (React.createElement(Badge, { key: k.id, colorScheme: (_a = k.colorScheme) !== null && _a !== void 0 ? _a : "gray" },
                            k.label,
                            ": ",
                            k.compute(filteredRows)));
                    }),
                    React.createElement(Button, { variant: "outline", onClick: this.reload, isDisabled: loading }, "Refresh"))),
            ((_b = report.actions) === null || _b === void 0 ? void 0 : _b.length) ? (React.createElement(React.Fragment, null,
                React.createElement(Divider, { my: 3 }),
                React.createElement(HStack, { spacing: 2, wrap: "wrap" }, report.actions.map((a) => {
                    var _a;
                    return (React.createElement(Button, { key: a.id, size: "sm", colorScheme: (_a = a.colorScheme) !== null && _a !== void 0 ? _a : "gray", onClick: () => a.onClick({ rows, filteredRows, reload: this.reload }) }, a.label));
                })))) : null,
            React.createElement(Divider, { my: 2 }),
            report.filters.length > 0 && React.createElement(Stack, { spacing: 3, direction: { base: "column", md: "row" } },
                report.filters.map((f) => {
                    var _a, _b, _c, _d, _e, _f, _g, _h;
                    return (React.createElement(FormControl, { key: f.id },
                        React.createElement(FormLabel, null, f.label),
                        f.type === "search" ? (React.createElement(Input, { placeholder: (_a = f.placeholder) !== null && _a !== void 0 ? _a : "Search...", value: (_b = params[f.id]) !== null && _b !== void 0 ? _b : "", onChange: (e) => this.setParam(f.id, e.target.value) })) : f.type === "select" ? (React.createElement(Select, { value: (_c = params[f.id]) !== null && _c !== void 0 ? _c : "", onChange: (e) => this.setParam(f.id, e.target.value) }, ((_d = f.options) !== null && _d !== void 0 ? _d : []).map((opt) => (React.createElement("option", { key: opt, value: opt === "All" ? "" : opt }, opt))))) : f.type === "multiselect" ? (React.createElement(MultiSelectDropdown, { label: f.label, options: ((_e = f.options) !== null && _e !== void 0 ? _e : []).filter((x) => x !== "All"), valueCsv: (_f = params[f.id]) !== null && _f !== void 0 ? _f : "", placeholder: (_g = f.placeholder) !== null && _g !== void 0 ? _g : "All", onChangeCsv: (csv) => this.setParam(f.id, csv) })) : f.type === "number" ? ((() => {
                            var _a, _b, _c;
                            // stored formats:
                            // gt:100 | gte:50 | lt:10 | lte:20 | eq:5 | between:10,50
                            const raw = (_a = params[f.id]) !== null && _a !== void 0 ? _a : "";
                            const [op0, rest0 = ""] = raw.split(":");
                            const op = op0 || "";
                            const [v1, v2] = rest0 ? rest0.split(",") : ["", ""];
                            const allowed = (_b = f.operators) !== null && _b !== void 0 ? _b : [
                                "gt",
                                "gte",
                                "lt",
                                "lte",
                                "eq",
                                "between"
                            ];
                            const setOp = (nextOp) => {
                                if (!nextOp) {
                                    this.setParam(f.id, "");
                                    return;
                                }
                                if (nextOp === "between") {
                                    // keep both slots
                                    this.setParam(f.id, "between:,");
                                    return;
                                }
                                // keep current v1 if present
                                this.setParam(f.id, `${nextOp}:${v1 || ""}`);
                            };
                            const setV1 = (next) => {
                                if (!op)
                                    return;
                                if (op === "between")
                                    this.setParam(f.id, `between:${next},${v2 || ""}`);
                                else
                                    this.debouncedSetNumberParam(f.id, `${op}:${next}`);
                                ;
                            };
                            const setV2 = (next) => {
                                if (op !== "between")
                                    return;
                                this.debouncedSetNumberParam(f.id, `${op}:${next}`);
                            };
                            return (React.createElement(HStack, { spacing: 2 },
                                React.createElement(Select, { value: op, onChange: (e) => setOp(e.target.value), w: "120px" },
                                    React.createElement("option", { value: "" }, (_c = f.placeholder) !== null && _c !== void 0 ? _c : "All"),
                                    allowed.includes("gt") && React.createElement("option", { value: "gt" }, ">"),
                                    allowed.includes("gte") && React.createElement("option", { value: "gte" }, "\u2265"),
                                    allowed.includes("lt") && React.createElement("option", { value: "lt" }, "<"),
                                    allowed.includes("lte") && React.createElement("option", { value: "lte" }, "\u2264"),
                                    allowed.includes("eq") && React.createElement("option", { value: "eq" }, "="),
                                    allowed.includes("between") && React.createElement("option", { value: "between" }, "Between")),
                                op === "between" ? (React.createElement(React.Fragment, null,
                                    React.createElement(Input, { type: "number", placeholder: "Min", onChange: (e) => {
                                            setV1(e.target.value);
                                        } }),
                                    React.createElement(Input, { type: "number", placeholder: "Max", onChange: (e) => setV2(e.target.value) }))) : (React.createElement(Input, { type: "number", placeholder: "Value", onChange: (e) => setV1(e.target.value), isDisabled: !op }))));
                        })()) : (React.createElement(Input, { type: "date", value: (_h = params[f.id]) !== null && _h !== void 0 ? _h : "", onChange: (e) => this.setParam(f.id, e.target.value) }))));
                }),
                React.createElement(FormControl, { display: "flex", alignItems: "end" },
                    React.createElement(Button, { colorScheme: "blue", onClick: () => {
                            this.setState({ loading: true });
                            this.applyClientFilters(rows, true);
                            this.setState({ loading: false });
                        }, isLoading: loading }, "Apply"))),
            err ? (React.createElement(Box, { mt: 3, p: 3, bg: "red.50", borderWidth: "1px", borderColor: "red.200", borderRadius: "md" },
                React.createElement(Text, { color: "red.700", fontSize: "sm" }, err))) : null,
            React.createElement(Divider, { my: 2 }),
            report.charts ? (React.createElement(React.Fragment, null,
                React.createElement(Divider, { my: 3 }),
                React.createElement(Box, null, this.builcCharts(report.charts)))) : null,
            loading ? (React.createElement(HStack, { mt: 4 },
                React.createElement(Spinner, null),
                React.createElement(Text, { color: "gray.600" }, "Loading..."))) : null,
            React.createElement(VirtualizedReportTable, { report: report, rows: filteredRows, height: (this.props.maxheight - (((_d = (_c = report.charts) === null || _c === void 0 ? void 0 : _c.length) !== null && _d !== void 0 ? _d : 0) > 0 ? 220 : 0)), onRowClick: report.rowDetails ? (row) => this.openDetails(row) : undefined }),
            report.rowDetails ? (React.createElement(Drawer, { isOpen: isOpen, placement: "right", onClose: this.closeDetails, size: "lg" },
                React.createElement(DrawerOverlay, null),
                React.createElement(DrawerContent, null,
                    React.createElement(DrawerCloseButton, null),
                    React.createElement(DrawerHeader, null, "Details"),
                    React.createElement(DrawerBody, null, selectedRow ? React.createElement(Box, { fontSize: "sm", dangerouslySetInnerHTML: { __html: report.rowDetails(selectedRow) } }) : React.createElement(Text, { color: "gray.600" }, "No row selected"))))) : null));
    }
}
